import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Users, Plus, Search, X, Loader2, Image, Shield, Sparkles,
  TrendingUp, Wallet, Coins, ArrowRight, Lock, BadgeCheck,
  ArrowUpDown, Clock, Activity, Star, Globe, AlertTriangle,
  RefreshCw, Heart, ZapOff, Bell,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useTipWallet } from '../hooks/useTipWallet';
import { useBCHPrice, getBCHPrice } from '../hooks/useBCHPrice';
import { useNip05Verified } from '../hooks/useNip05Verified';
import { uploadImage } from '../lib/imageUpload';
import { BCHLogo } from '../components/Layout';
import {
  COMMUNITY_DEFINITION_KIND,
  COMMUNITY_POST_KIND,
  MEMBERSHIP_KIND,
  COMMUNITY_CREATION_FEE_USD,
  PLATFORM_WALLET,
  MIN_JOIN_FEE_USD,
  parseCommunity,
  parseMembership,
  communitySlug,
  buildCommunityDefinitionTags,
  membershipDTag,
  canCreateCommunity,
  usdToSats,
  type Community,
  type CommunityType,
} from '../lib/communities';
import { useBCHAddress } from '../hooks/useBCHAddress';

type SortMode = 'active' | 'members' | 'newest';
type TabMode = 'favorites' | 'active' | 'inactive' | 'all';

// ── Favorites (localStorage) ──────────────────────────────────────
const FAVORITES_KEY = 'bchnostr:community-favorites';

function getFavorites(): Set<string> {
  try {
    const raw = localStorage.getItem(FAVORITES_KEY);
    if (raw) return new Set(JSON.parse(raw));
  } catch {}
  return new Set();
}

function saveFavorites(favs: Set<string>) {
  try { localStorage.setItem(FAVORITES_KEY, JSON.stringify([...favs])); } catch {}
}

// ── Last-visited timestamps (for unread badges) ───────────────────
const LAST_VISIT_KEY = 'bchnostr:community-last-visit';

function getLastVisits(): Record<string, number> {
  try {
    const raw = localStorage.getItem(LAST_VISIT_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return {};
}

function markVisited(communityId: string) {
  try {
    const visits = getLastVisits();
    visits[communityId] = Math.floor(Date.now() / 1000);
    localStorage.setItem(LAST_VISIT_KEY, JSON.stringify(visits));
  } catch {}
}

export default function CommunitiesPage() {
  const { user, setShowLoginDialog } = useAppContext();
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [sortMode, setSortMode] = useState<SortMode>('active');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [activeTab, setActiveTab] = useState<TabMode>('favorites');
  const [favorites, setFavorites] = useState<Set<string>>(getFavorites);
  const [lastVisits] = useState<Record<string, number>>(getLastVisits);

  const toggleFavorite = useCallback((communityId: string) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(communityId)) next.delete(communityId);
      else next.add(communityId);
      saveFavorites(next);
      return next;
    });
  }, []);

  // If user has no favorites, default to 'all' tab so communities are always visible
  const effectiveTab = (activeTab === 'favorites' && favorites.size === 0 && !search) ? 'all' : activeTab;

  // Relays known to index community events — query broadly so posts
  // don't "disappear" when a single relay misses them
  const communityRelays = [
    'wss://relay.bchnostr.com',
    'wss://nos.lol',
    'wss://relay.primal.net',
    'wss://relay.nostr.net',
  ];

  // Fetch only BCH Nostr communities (tagged with 'bch-community')
  const { events: communityEvents, loading } = useNostrQuery({
    filters: [{ kinds: [COMMUNITY_DEFINITION_KIND], '#t': ['bch-community'], limit: 200 }],
    allRelays: true,
    extraRelays: communityRelays,
  });

  // Fetch all membership events (to count members)
  const { events: membershipEvents } = useNostrQuery({
    filters: [{ kinds: [MEMBERSHIP_KIND], '#t': ['bch-community-member'], limit: 1000 }],
    allRelays: true,
    extraRelays: communityRelays,
  });

  // Deduplicate communities by a-tag (keep newest per creator:d-tag)
  const communities = useMemo(() => {
    const map = new Map<string, Community>();
    for (const ev of communityEvents) {
      const c = parseCommunity(ev);
      if (c.archived) continue;
      const existing = map.get(c.id);
      if (!existing || ev.created_at > existing.createdAt) {
        map.set(c.id, c);
      }
    }
    return [...map.values()];
  }, [communityEvents]);

  // Fetch community posts for activity counting.
  // Query by community a-tags (uppercase A), not by 't' tag — community
  // posts don't carry the 'bch-community' t-tag, only the community
  // definition events do.
  const communityIds = useMemo(() => communities.map(c => c.id), [communities]);
  const [postsSince] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 7);
  const { events: recentPostEvents } = useNostrQuery({
    filters: communityIds.length > 0 ? [
      { kinds: [COMMUNITY_POST_KIND], '#A': communityIds, since: postsSince, limit: 500 },
    ] : [],
    enabled: communityIds.length > 0,
    allRelays: true,
    extraRelays: communityRelays,
  });

  // Count members per community
  const memberCounts = useMemo(() => {
    const counts = new Map<string, Set<string>>();
    for (const ev of membershipEvents) {
      const m = parseMembership(ev);
      if (!m) continue;
      if (!counts.has(m.communityId)) counts.set(m.communityId, new Set());
      counts.get(m.communityId)!.add(m.pubkey);
    }
    return counts;
  }, [membershipEvents]);

  // Count recent posts per community
  const activityCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of recentPostEvents) {
      // Find the community a-tag (uppercase A is the canonical tag for community posts)
      const aTag = ev.tags?.find((t: string[]) => t[0] === 'A' || t[0] === 'a')?.[1];
      if (aTag) {
        counts.set(aTag, (counts.get(aTag) || 0) + 1);
      }
    }
    return counts;
  }, [recentPostEvents]);

  // Compute new post counts per community (posts since last visit)
  const newPostCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of recentPostEvents) {
      const aTag = ev.tags?.find((t: string[]) => t[0] === 'A' || t[0] === 'a')?.[1];
      if (!aTag) continue;
      const lastVisit = lastVisits[aTag] || 0;
      if (ev.created_at > lastVisit) {
        counts.set(aTag, (counts.get(aTag) || 0) + 1);
      }
    }
    return counts;
  }, [recentPostEvents, lastVisits]);

  // Filter by search
  const filtered = useMemo(() => {
    let result = communities;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
      );
    }
    return result;
  }, [communities, search]);

  // Apply tab filter
  const tabFiltered = useMemo(() => {
    // When searching, show all results regardless of tab
    if (search.trim()) return filtered;

    switch (effectiveTab) {
      case 'favorites':
        return filtered.filter(c => favorites.has(c.id));
      case 'active':
        // Communities with any activity in the last 7 days
        return filtered.filter(c => (activityCounts.get(c.id) || 0) > 0);
      case 'inactive':
        // Communities with zero activity in the last 7 days
        return filtered.filter(c => (activityCounts.get(c.id) || 0) === 0);
      case 'all':
      default:
        return filtered;
    }
  }, [filtered, effectiveTab, favorites, activityCounts, search]);

  // Sort
  const sorted = useMemo(() => {
    return [...tabFiltered].sort((a, b) => {
      switch (sortMode) {
        case 'active': {
          const actA = activityCounts.get(a.id) || 0;
          const actB = activityCounts.get(b.id) || 0;
          return actB - actA || b.createdAt - a.createdAt;
        }
        case 'members': {
          const memA = memberCounts.get(a.id)?.size || 0;
          const memB = memberCounts.get(b.id)?.size || 0;
          return memB - memA || b.createdAt - a.createdAt;
        }
        case 'newest':
        default:
          return b.createdAt - a.createdAt;
      }
    });
  }, [tabFiltered, sortMode, activityCounts, memberCounts]);

  // Communities user is a member of
  const myMemberships = useMemo(() => {
    if (!user) return new Set<string>();
    const set = new Set<string>();
    for (const ev of membershipEvents) {
      if (ev.pubkey !== user.pubkey) continue;
      const m = parseMembership(ev);
      if (m) set.add(m.communityId);
    }
    return set;
  }, [membershipEvents, user]);

  // Tab counts
  const tabCounts = useMemo(() => {
    const searchFiltered = search.trim()
      ? communities.filter(c => c.name.toLowerCase().includes(search.toLowerCase()) || c.description.toLowerCase().includes(search.toLowerCase()))
      : communities;
    return {
      favorites: searchFiltered.filter(c => favorites.has(c.id)).length,
      active: searchFiltered.filter(c => (activityCounts.get(c.id) || 0) > 0).length,
      inactive: searchFiltered.filter(c => (activityCounts.get(c.id) || 0) === 0).length,
      all: searchFiltered.length,
    };
  }, [communities, favorites, activityCounts, search]);

  // Total unread across favorites
  const totalUnread = useMemo(() => {
    let count = 0;
    favorites.forEach(id => { count += newPostCounts.get(id) || 0; });
    return count;
  }, [favorites, newPostCounts]);

  const sortLabels: Record<SortMode, { label: string; icon: React.ReactNode }> = {
    active: { label: 'Most Active', icon: <Activity className="w-3.5 h-3.5" /> },
    members: { label: 'Most Members', icon: <Users className="w-3.5 h-3.5" /> },
    newest: { label: 'Newest', icon: <Clock className="w-3.5 h-3.5" /> },
  };

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 lg:top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border">
        <div className="px-4 py-3 flex items-center justify-between">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Community Spaces
          </h1>
          <button
            onClick={() => user ? setShowCreate(true) : setShowLoginDialog(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
        {/* Search + Sort */}
        <div className="px-4 pb-3 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search communities..."
              className="w-full pl-9 pr-8 py-2 bg-muted rounded-lg text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Sort dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="flex items-center gap-1.5 px-3 py-2 bg-muted rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {sortLabels[sortMode].icon}
              <span className="hidden sm:inline">{sortLabels[sortMode].label}</span>
              <ArrowUpDown className="w-3 h-3" />
            </button>
            {showSortMenu && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setShowSortMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-30 bg-popover border border-border rounded-lg shadow-lg overflow-hidden min-w-[150px]">
                  {(Object.keys(sortLabels) as SortMode[]).map(mode => (
                    <button
                      key={mode}
                      onClick={() => { setSortMode(mode); setShowSortMenu(false); }}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted transition-colors ${sortMode === mode ? 'text-primary font-bold bg-primary/5' : 'text-foreground'}`}
                    >
                      {sortLabels[mode].icon}
                      {sortLabels[mode].label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-center gap-1 p-1 bg-muted/60 rounded-xl overflow-x-auto">
          <button
            onClick={() => setActiveTab('favorites')}
            className={`flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
              effectiveTab === 'favorites'
                ? 'bg-background text-primary shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${effectiveTab === 'favorites' ? 'fill-primary' : ''}`} />
            Favorites
            {favorites.size > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                effectiveTab === 'favorites' ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
              }`}>{favorites.size}</span>
            )}
            {totalUnread > 0 && effectiveTab !== 'favorites' && (
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">{totalUnread > 99 ? '99+' : totalUnread}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('active')}
            className={`flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
              effectiveTab === 'active'
                ? 'bg-background text-emerald-500 shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Activity className="w-3.5 h-3.5" />
            Active
            {tabCounts.active > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                effectiveTab === 'active' ? 'bg-emerald-500/10 text-emerald-500' : 'bg-muted text-muted-foreground'
              }`}>{tabCounts.active}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('inactive')}
            className={`flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
              effectiveTab === 'inactive'
                ? 'bg-background text-amber-500 shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <ZapOff className="w-3.5 h-3.5" />
            Inactive
            {tabCounts.inactive > 0 && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                effectiveTab === 'inactive' ? 'bg-amber-500/10 text-amber-500' : 'bg-muted text-muted-foreground'
              }`}>{tabCounts.inactive}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('all')}
            className={`flex items-center gap-1 px-2.5 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
              effectiveTab === 'all'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            All
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              effectiveTab === 'all' ? 'bg-foreground/10 text-foreground' : 'bg-muted text-muted-foreground'
            }`}>{tabCounts.all}</span>
          </button>
        </div>
      </div>

      {/* Community grid */}
      <div className="px-4 pt-2 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : sorted.length === 0 ? (
          <div className="text-center py-16 px-4">
            {effectiveTab === 'favorites' && favorites.size === 0 ? (
              <>
                <Heart className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm font-medium">No favorites yet</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Tap the <Heart className="w-3 h-3 inline" /> on any community to add it to your favorites.
                </p>
                <button
                  onClick={() => setActiveTab('all')}
                  className="mt-3 text-primary text-xs font-medium hover:underline"
                >
                  Browse all communities →
                </button>
              </>
            ) : effectiveTab === 'active' ? (
              <>
                <Activity className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm font-medium">No active communities this week</p>
                <p className="text-xs text-muted-foreground mt-1">Communities with posts in the last 7 days show here.</p>
              </>
            ) : effectiveTab === 'inactive' ? (
              <>
                <ZapOff className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm font-medium">No inactive communities</p>
                <p className="text-xs text-muted-foreground mt-1">All communities have been active this week!</p>
              </>
            ) : search ? (
              <>
                <Users className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm font-medium">No communities found</p>
                <p className="text-xs text-muted-foreground mt-1">Try a different search term</p>
              </>
            ) : (
              <>
                <Users className="w-12 h-12 mx-auto text-muted-foreground/20 mb-3" />
                <p className="text-sm font-medium">No communities yet</p>
                <p className="text-xs text-muted-foreground mt-1">Be the first to create one!</p>
                <button
                  onClick={() => user ? setShowCreate(true) : setShowLoginDialog(true)}
                  className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Create a Community
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {sorted.map(c => (
              <CommunityCard
                key={c.id}
                community={c}
                memberCount={memberCounts.get(c.id)?.size || 0}
                activityCount={activityCounts.get(c.id) || 0}
                isFavorite={favorites.has(c.id)}
                onToggleFavorite={() => toggleFavorite(c.id)}
                newPostCount={newPostCounts.get(c.id) || 0}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreate && user && (
        <CreateCommunityModal onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

// ── Community Card (Box / Tile) ──────────────────────────────────

function CommunityCard({
  community,
  memberCount,
  activityCount,
  isFavorite,
  onToggleFavorite,
  newPostCount,
}: {
  community: Community;
  memberCount: number;
  activityCount: number;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
  newPostCount?: number;
}) {
  return (
    <div className="relative">
      {/* Favorite button */}
      {onToggleFavorite && (
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite(); }}
          className={`absolute top-2 right-2 z-10 p-1.5 rounded-full backdrop-blur-sm transition-all ${
            isFavorite
              ? 'bg-red-500/90 text-white shadow-md'
              : 'bg-black/40 text-white/70 hover:bg-black/60 hover:text-white'
          }`}
          title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Heart className={`w-3.5 h-3.5 ${isFavorite ? 'fill-current' : ''}`} />
        </button>
      )}

      {/* Unread badge */}
      {(newPostCount || 0) > 0 && (
        <div className="absolute top-2 left-2 z-10 px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold shadow-md flex items-center gap-0.5">
          <Bell className="w-2.5 h-2.5" />
          {newPostCount! > 99 ? '99+' : newPostCount}
        </div>
      )}

      <Link
        to={`/community/${encodeURIComponent(community.id)}`}
        onClick={() => markVisited(community.id)}
        className="flex flex-col rounded-xl border border-border hover:border-primary/30 hover:shadow-md transition-all group overflow-hidden bg-card"
      >
        {/* Image / Placeholder */}
        {community.image ? (
          <img src={community.image} alt="" className="w-full h-24 object-cover" />
        ) : (
          <div className="w-full h-24 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent flex items-center justify-center">
            <Users className="w-8 h-8 text-primary/30" />
          </div>
        )}

        {/* Content */}
        <div className="p-3 flex flex-col flex-1">
          {/* Name + type badge */}
          <h3 className="font-semibold text-sm truncate group-hover:text-primary transition-colors">
            {community.name}
          </h3>

          {/* Description */}
          {community.description && (
            <p className="text-[11px] text-muted-foreground line-clamp-2 mt-1 leading-snug flex-1">
              {community.description}
            </p>
          )}

          {/* Stats row */}
          <div className="flex items-center gap-2 mt-2 text-[10px] text-muted-foreground flex-wrap">
            <span className="flex items-center gap-0.5">
              <Users className="w-3 h-3" /> {memberCount}
            </span>
            {activityCount > 0 && (
              <span className="flex items-center gap-0.5">
                <Activity className="w-3 h-3" /> {activityCount}/wk
              </span>
            )}
            {community.type === 'premium' ? (
              <span className="ml-auto flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                <Lock className="w-2.5 h-2.5" /> ${community.joinFeeUsd}
              </span>
            ) : (
              <span className="ml-auto flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                <Globe className="w-2.5 h-2.5" /> Open
              </span>
            )}
          </div>
        </div>
      </Link>
    </div>
  );
}

// ── Create Community Modal ───────────────────────────────────────

function CreateCommunityModal({ onClose }: { onClose: () => void }) {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const tipWallet = useTipWallet();
  const { profile } = useNostrProfile(user?.pubkey || '');
  const nip05Value = typeof profile?.nip05 === 'string' ? profile.nip05 : '';
  const isVerified = useNip05Verified(nip05Value, user?.pubkey);
  const { address: userBchAddress } = useBCHAddress(user?.pubkey);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState('');
  const [image, setImage] = useState('');
  const [communityType, setCommunityType] = useState<CommunityType>('open');
  const [joinFeeUsd, setJoinFeeUsd] = useState('1.00');
  const [uploading, setUploading] = useState(false);
  const [step, setStep] = useState<'form' | 'payment' | 'paying' | 'publishing' | 'publish-failed' | 'done'>('form');
  const [error, setError] = useState('');
  const [walletInitializing, setWalletInitializing] = useState(false);
  // Track payment TXID so we can show proof + retry publish after payment succeeded
  const [paymentTxid, setPaymentTxid] = useState('');
  // Store the generated d-tag so retries publish the same community
  const dTagRef = useRef('');

  const price = getBCHPrice();
  const feeSats = price ? Math.round((COMMUNITY_CREATION_FEE_USD / price) * 1e8) : 0;
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = walletBalance >= feeSats + 300;

  const eligibility = canCreateCommunity(isVerified);
  const allRequirementsMet = eligibility.eligible;

  /** Wrap a promise with a timeout so it can't hang forever */
  const withTimeout = <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> => {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error(`${label} timed out after ${Math.round(ms / 1000)}s. Please try again.`)), ms);
      promise.then(
        (val) => { clearTimeout(timer); resolve(val); },
        (err) => { clearTimeout(timer); reject(err); },
      );
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setImage(url);
    } catch (err) {
      setError('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleInitWallet = async () => {
    setWalletInitializing(true);
    try { await tipWallet.init(); } catch {}
    setWalletInitializing(false);
  };

  const handleProceedToPayment = () => {
    if (!name.trim()) return;
    if (!allRequirementsMet) return;
    setError('');
    setStep('payment');
  };

  /** Publish the community definition + membership events to Nostr.
   *  Retries up to 3 times — relay publish can be flaky. */
  const publishCommunity = async (): Promise<void> => {
    if (!user) throw new Error('Not logged in');

    // Generate d-tag only once — reuse on retries so we don't create duplicates
    if (!dTagRef.current) {
      dTagRef.current = communitySlug(name) + '-' + Date.now().toString(36);
    }
    const dTag = dTagRef.current;
    const parsedJoinFee = communityType === 'premium' ? parseFloat(joinFeeUsd) : 0;

    const tags = buildCommunityDefinitionTags({
      dTag,
      name: name.trim(),
      description: description.trim(),
      rules: rules.trim(),
      image: image || undefined,
      creatorPubkey: user.pubkey,
      type: communityType,
      joinFeeUsd: parsedJoinFee > 0 ? parsedJoinFee : undefined,
      ownerAddress: userBchAddress || '',
    });

    // Retry wrapper for flaky relay connections
    const publishWithRetry = async (event: any, label: string) => {
      let lastErr: any;
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          await withTimeout(publish(event), 30_000, label);
          return; // success
        } catch (err) {
          lastErr = err;
          if (attempt < 2) await new Promise(r => setTimeout(r, 1500 * (attempt + 1)));
        }
      }
      throw lastErr;
    };

    await publishWithRetry({
      kind: COMMUNITY_DEFINITION_KIND,
      content: '',
      tags,
      created_at: Math.floor(Date.now() / 1000),
    }, 'Publishing community');

    // Also publish creator's own membership
    const communityId = `${COMMUNITY_DEFINITION_KIND}:${user.pubkey}:${dTag}`;
    await publishWithRetry({
      kind: MEMBERSHIP_KIND,
      content: JSON.stringify({ role: 'owner' }),
      tags: [
        ['d', membershipDTag(communityId)],
        ['a', communityId],
        ['t', 'bch-community-member'],
      ],
      created_at: Math.floor(Date.now() / 1000),
    }, 'Publishing membership');
  };

  const handlePay = async () => {
    if (!user || !name.trim()) return;
    setStep('paying');
    setError('');
    setPaymentTxid('');

    try {
      if (!tipWallet.ready) {
        await withTimeout(tipWallet.init(), 30_000, 'Wallet initialization');
      }

      // Pay the creation fee to platform (60s timeout — Electrum can be slow)
      const txid = await withTimeout(
        tipWallet.sendBCH(PLATFORM_WALLET, BigInt(feeSats)),
        60_000,
        'BCH payment',
      );
      setPaymentTxid(txid || 'confirmed');
    } catch (err) {
      // Payment failed or timed out — no money was sent (or it's unconfirmed)
      const msg = err instanceof Error ? err.message : 'Payment failed';
      setError(msg);
      setStep('payment');
      return;
    }

    // Payment succeeded! Now publish the community to Nostr.
    // This is a separate try-catch so if publish fails, we don't lose the payment.
    setStep('publishing');
    try {
      await publishCommunity();
      tipWallet.refreshBalance();
      setStep('done');
    } catch (err) {
      // Payment was taken but publish failed — show retry UI
      const msg = err instanceof Error ? err.message : 'Failed to publish community';
      setError(msg);
      setStep('publish-failed');
    }
  };

  /** Retry just the publish step (payment was already made) */
  const handleRetryPublish = async () => {
    setStep('publishing');
    setError('');
    try {
      await publishCommunity();
      tipWallet.refreshBalance();
      setStep('done');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to publish community';
      setError(msg);
      setStep('publish-failed');
    }
  };

  // Prevent accidentally closing while payment/publish is in progress
  const isProcessing = step === 'paying' || step === 'publishing';
  const safeClose = () => {
    if (isProcessing) return; // Can't close while processing
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={safeClose}>
      <div className="bg-card border border-border rounded-t-2xl sm:rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <h2 className="text-base font-bold flex items-center gap-2">
            <Sparkles className="w-4.5 h-4.5 text-primary" />
            {step === 'done' ? 'Community Created!' : step === 'publish-failed' ? 'Almost There...' : 'Create Community'}
          </h2>
          <button onClick={safeClose} className={`p-1.5 rounded-lg hover:bg-muted ${isProcessing ? 'opacity-30 cursor-not-allowed' : ''}`} disabled={isProcessing}>
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 pb-5 space-y-4">
          {/* ── Step: Done ── */}
          {step === 'done' ? (
            <div className="text-center py-6 space-y-3">
              <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
                <Users className="w-7 h-7 text-emerald-500" />
              </div>
              <p className="text-sm font-bold text-emerald-500">Your community is live!</p>
              <p className="text-xs text-muted-foreground">
                <strong>{name}</strong> is ready. Share it with the BCH community!
              </p>
              <button
                onClick={onClose}
                className="mt-2 px-6 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                Done
              </button>
            </div>
          ) : step === 'publish-failed' ? (
            /* ── Step: Publish Failed (payment succeeded but Nostr publish failed) ── */
            <div className="space-y-4">
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-2">
                <h3 className="text-sm font-bold flex items-center gap-2 text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="w-4 h-4" /> Payment received, but community wasn't created
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Your BCH payment was successful, but the community event failed to publish to Nostr.
                  Don't worry — your payment is safe. Please tap "Retry" to finish creating the community.
                </p>
                {paymentTxid && paymentTxid !== 'confirmed' && (
                  <p className="text-[10px] text-muted-foreground font-mono break-all">
                    Payment TXID: {paymentTxid}
                  </p>
                )}
              </div>
              {error && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}
              <button
                onClick={handleRetryPublish}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
              >
                <RefreshCw className="w-4 h-4" /> Retry Creating Community
              </button>
              <p className="text-[10px] text-muted-foreground text-center">
                If this keeps failing, please contact support with your payment TXID.
              </p>
            </div>
          ) : step === 'payment' || step === 'paying' || step === 'publishing' ? (
            <>
              {/* ── Step: Payment ── */}
              <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
                <div className="flex items-center gap-3">
                  {image ? (
                    <img src={image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Users className="w-5 h-5 text-primary/60" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="font-semibold text-sm truncate">{name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {communityType === 'premium' ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 font-bold border border-amber-500/20">
                          Premium · ${joinFeeUsd}/join
                        </span>
                      ) : (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold border border-emerald-500/20">
                          Open
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Fee info */}
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Community creation fee</span>
                  <span className="font-bold font-mono">
                    ${COMMUNITY_CREATION_FEE_USD}{feeSats > 0 ? ` (~${feeSats.toLocaleString()} sats)` : ''}
                  </span>
                </div>
                <p className="text-[10px] text-muted-foreground">
                  This one-time fee prevents spam and shows you're committed to building a great community.
                </p>
              </div>

              {/* Wallet */}
              {!tipWallet.ready ? (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground">Your BCH wallet needs to be initialized to pay the fee.</p>
                  <button
                    onClick={handleInitWallet}
                    disabled={walletInitializing}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-muted text-sm font-medium hover:bg-muted/80 transition-colors"
                  >
                    {walletInitializing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
                    {walletInitializing ? 'Initializing...' : 'Initialize Wallet'}
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Wallet balance</span>
                    <span className="font-mono font-semibold">{walletBalance.toLocaleString()} sats</span>
                  </div>
                  {!canAfford && (
                    <div className="flex items-start gap-2 text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2 border border-amber-500/20">
                      <Coins className="w-4 h-4 shrink-0 mt-0.5" />
                      <p>Not enough BCH. You need at least <strong>{feeSats.toLocaleString()} sats</strong>.</p>
                    </div>
                  )}
                </div>
              )}

              {error && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}

              <div className="flex gap-2">
                <button
                  onClick={() => { setStep('form'); setError(''); }}
                  disabled={step === 'paying' || step === 'publishing'}
                  className="px-4 py-2.5 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  onClick={handlePay}
                  disabled={!tipWallet.ready || !canAfford || step === 'paying' || step === 'publishing'}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {step === 'paying' ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Paying...</>
                  ) : step === 'publishing' ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</>
                  ) : (
                    <><BCHLogo size={16} /> Pay ${COMMUNITY_CREATION_FEE_USD} &amp; Create</>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              {/* ── Step: Form ── */}
              {/* Requirements check */}
              {!allRequirementsMet && (
                <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 space-y-2">
                  <h3 className="text-xs font-bold flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="w-3.5 h-3.5" /> Requirements
                  </h3>
                  <div className="space-y-1.5">
                    {eligibility.requirements.map((req, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 ${
                          req.met ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'
                        }`}>
                          {req.met ? '✓' : '\u2717'}
                        </div>
                        <span className={req.met ? 'text-muted-foreground' : 'text-foreground font-medium'}>
                          {req.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Banner Image */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Banner Image</label>
                {image ? (
                  <div className="relative mt-1.5">
                    <img src={image} alt="" className="w-full h-32 rounded-xl object-cover" />
                    <button
                      onClick={() => setImage('')}
                      className="absolute top-2 right-2 p-1 bg-black/60 rounded-full text-white hover:bg-black/80"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <label className="mt-1.5 flex items-center justify-center gap-2 w-full h-24 rounded-xl border-2 border-dashed border-border hover:border-primary/40 cursor-pointer transition-colors">
                    <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                    {uploading ? (
                      <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    ) : (
                      <>
                        <Image className="w-5 h-5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">Upload image</span>
                      </>
                    )}
                  </label>
                )}
              </div>

              {/* Name */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Community Name *</label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. BCH Merchants"
                  maxLength={60}
                  className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Description *</label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="What's this community about?"
                  rows={3}
                  maxLength={500}
                  className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Rules */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Rules</label>
                <textarea
                  value={rules}
                  onChange={e => setRules(e.target.value)}
                  placeholder="Community rules (one per line)"
                  rows={3}
                  maxLength={1000}
                  className="w-full bg-muted rounded-lg px-3 py-2.5 text-sm mt-1 resize-none placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>

              {/* Community Type */}
              <div>
                <label className="text-xs font-medium text-muted-foreground">Community Type</label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  <button
                    onClick={() => setCommunityType('open')}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      communityType === 'open'
                        ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                        : 'border-border hover:border-primary/30'
                    }`}
                  >
                    <Globe className={`w-5 h-5 mb-1 ${communityType === 'open' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className="text-xs font-bold">Open</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Free to join</p>
                  </button>
                  <button
                    onClick={() => setCommunityType('premium')}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      communityType === 'premium'
                        ? 'border-amber-500 bg-amber-500/5 ring-1 ring-amber-500/30'
                        : 'border-border hover:border-amber-500/30'
                    }`}
                  >
                    <Lock className={`w-5 h-5 mb-1 ${communityType === 'premium' ? 'text-amber-500' : 'text-muted-foreground'}`} />
                    <p className="text-xs font-bold">Premium</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Paid join fee</p>
                  </button>
                </div>
              </div>

              {/* Join fee (premium only) */}
              {communityType === 'premium' && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Join Fee (USD)</label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground font-bold">$</span>
                    <input
                      type="number"
                      min={MIN_JOIN_FEE_USD}
                      step="0.50"
                      value={joinFeeUsd}
                      onChange={e => setJoinFeeUsd(e.target.value)}
                      className="w-full pl-7 pr-3 py-2.5 bg-muted rounded-lg text-sm font-mono focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    You receive 80% of join fees · BCH Nostr receives 20%
                  </p>
                </div>
              )}

              {error && <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>}

              <button
                onClick={handleProceedToPayment}
                disabled={!name.trim() || !description.trim() || !allRequirementsMet}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

